import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, LayoutGrid, FileText, LifeBuoy, User, Sun } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import LanguageToggle from '@/components/LanguageToggle';
import { UserMenu } from '@/components/UserMenu';
import { BroadcastTicker } from '@/components/BroadcastNews';
import { useLang } from '@/i18n/LangContext';
import NotificationsBell from '@/components/NotificationsBell';

export default function CustomerLayout() {
    const { user } = useAuth();
    const { t } = useLang();
    const location = useLocation();
    const tabs = [
        { to: '/customer/home',         icon: Home,       label: t('home'),          aliases: ['/customer'] },
        { to: '/customer/services',     icon: LayoutGrid, label: t('quick_actions'), matchers: ['/customer/services', '/customer/links', '/customer/vendors', '/customer/quotations', '/customer/survey', '/customer/generation', '/customer/refer', '/customer/subsidy', '/customer/ai', '/customer/learn', '/customer/bbps', '/customer/wallet', '/customer/loans'] },
        { to: '/customer/applications', icon: FileText,   label: t('applications'),  matchers: ['/customer/applications', '/customer/apply', '/customer/projects'] },
        { to: '/customer/support',      icon: LifeBuoy,   label: t('support'),       matchers: ['/customer/support', '/customer/complaints'] },
        { to: '/customer/profile',      icon: User,       label: t('profile') },
    ];
    // Matches a tab if URL equals any alias, equals the primary `to`,
    // or starts with any matcher followed by `/`.
    const isActiveTab = (tab) => {
        const p = location.pathname;
        if (Array.isArray(tab.aliases) && tab.aliases.includes(p)) return true;
        const matchers = (tab.matchers && tab.matchers.length ? tab.matchers : [tab.to]);
        return matchers.some(m => p === m || p.startsWith(m + '/'));
    };

    return (
        <div className='app-shell min-h-screen flex flex-col' style={{ background: '#FAFAF7' }} data-testid='customer-layout'>
            <BroadcastTicker />

            {/* Top navbar — subtle brand strip in navy w/ orange sun mark */}
            <header
                className='sticky top-0 z-30 border-b'
                style={{
                    background: 'linear-gradient(180deg,#FFFFFF 0%,#FFFBF3 100%)',
                    borderColor: 'rgba(30,46,122,.10)'
                }}
                data-testid='customer-header'
            >
                <div className='max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3'>
                    <div className='flex items-center gap-2'>
                        {/* Real UPPower logo */}
                        <img
                            src='/uppower-logo.png'
                            alt='UPPower Solar'
                            className='h-9 sm:h-10 w-auto select-none'
                            draggable='false'
                        />
                    </div>
                    <div className='flex items-center gap-2'>
                        <LanguageToggle />
                        <NotificationsBell />
                        <UserMenu />
                    </div>
                </div>
            </header>

            <main className='flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-tabbar' data-testid='customer-main'>
                <Outlet />
            </main>

            {/* Bottom tabbar — brand-aligned active state (orange pill + top bar) */}
            <nav
                className='fixed bottom-0 inset-x-0 z-40'
                style={{
                    background: 'rgba(255,255,255,.97)',
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid rgba(30,46,122,.10)',
                    boxShadow: '0 -2px 12px -6px rgba(30,46,122,.15)'
                }}
                data-testid='customer-tabbar'
            >
                <div className='max-w-6xl mx-auto grid grid-cols-5'>
                    {tabs.map(tab => {
                        const active = isActiveTab(tab);
                        return (
                            <NavLink
                                to={tab.to}
                                key={tab.to}
                                data-testid={`tab-${tab.to.split('/').pop()}`}
                                className='flex flex-col items-center justify-center py-2.5 min-h-[62px] gap-1 text-[11px] relative transition-colors'
                            >
                                {/* Active top-bar accent */}
                                {active && (
                                    <span
                                        className='absolute top-0 left-1/2 -translate-x-1/2 h-1 w-8 rounded-b-full'
                                        style={{ background: 'linear-gradient(90deg,#F26522,#FBBF24)' }}
                                    />
                                )}
                                <span
                                    className={cn(
                                        'w-9 h-9 rounded-xl flex items-center justify-center transition-colors',
                                        active ? '' : 'text-slate-500'
                                    )}
                                    style={active ? { background: 'linear-gradient(135deg,#F26522,#FBBF24)', color: '#1E2E7A' } : undefined}
                                >
                                    <tab.icon className='w-4.5 h-4.5' style={{ width: 18, height: 18 }} />
                                </span>
                                <span
                                    className={cn('leading-none whitespace-nowrap font-medium', active ? '' : 'text-slate-500')}
                                    style={active ? { color: '#1E2E7A' } : undefined}
                                >
                                    {tab.label}
                                </span>
                            </NavLink>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}
